<template>
  <div class="multi-selection-table-container no-select scroll-fire-fox">
    <table
      v-if="
        !loading && fields.length > 0 && availableAssets.length > 0 && !editSelectedOpen
      "
      class="multi-selection-table"
      style="position: relative"
      id="table"
    >
      <tr class="table-header">
        <th class="object-index" />

        <th>
          ID
          <!--<v-btn text icon @click="sortTable(1)">
          <v-icon>keyboard_arrow_down</v-icon>
          </v-btn>-->
        </th>

        <th
          v-for="(field, index) in fields"
          :key="index + field.label"
          class="header-cell"
          :class="{ required: field.required }"
        >
          {{ field.label }}
          <!--<v-btn text icon @click="sortTable(1)">
          <v-icon>keyboard_arrow_down</v-icon>
          </v-btn>-->
        </th>
      </tr>

      <tr
        class="table-body"
        v-for="(object, objectIndex) in availableAssets"
        :key="objectIndex"
        :class="{
          selected: selectedIndexes.includes(objectIndex)
        }"
      >
        <td class="object-index" @click="select(objectIndex, $event)">
          {{ objectIndex + 1 }}
        </td>

        <td class="input-disabled" @click="select(objectIndex, $event)">
          {{ object[prefix + "_id"] }}
        </td>

        <td
          v-for="(field, fieldIndex) in fields"
          :key="fieldIndex + field.label"
          :class="editionMode ? field.size : ''"
          :ref="getInputRef(objectIndex, fieldIndex)"
        >
          <div
            class="input-disabled with-select"
            v-if="
              (!editionMode || (editionMode && field.disable)) &&
                field.input_type !== 'checkbox'
            "
            @click="select(objectIndex, $event)"
          >
            {{ getNonEditableValue(object, field) }}
          </div>

          <input
            v-else-if="isStandardInput(field.input_type)"
            :type="getSpecificStandardInputType(field.input_type)"
            :value="getValue(object, field)"
            @change="setValue(objectIndex, field, $event.target.value)"
            class="table-input"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            @keydown.ctrl.up.prevent="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />

          <treeselect
            v-else-if="isSelectInput(field.input_type)"
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="getSpecificListOfOptions(field)"
            :clearable="selectIsClearable(field)"
            :disabled="field.multi_select_disable"
            class="multi-selection-select"
            :class="{
              edited: valueWasChanged(objectIndex, field),
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
            @keydown.ctrl.up.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1000)
            "
            @keydown.ctrl.down.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1000)
            "
            @keydown.ctrl.left.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, -1)
            "
            @keydown.ctrl.right.prevent.native="
              changePositionFocus(objectIndex, fieldIndex, 1)
            "
          />

          <div
            v-if="field.input_type === 'checkbox'"
            class="multi-selection-checkbox-container"
            :class="{ edited: valueWasChanged(objectIndex, field) }"
          >
            <v-checkbox
              :input-value="getValue(object, field)"
              @change="setValue(objectIndex, field, $event)"
              class="multi-selection-checkbox"
              style="z-index: 100"
              color="primary"
              :ripple="false"
              :disabled="!editionMode"
              :hide-details="true"
              disableFuzzyMatching
              @keydown.ctrl.up.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, -1000)
              "
              @keydown.ctrl.down.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, 1000)
              "
              @keydown.ctrl.left.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, -1)
              "
              @keydown.ctrl.right.prevent.native="
                changePositionFocus(objectIndex, fieldIndex, 1)
              "
            />
          </div>
        </td>
      </tr>
    </table>

    <div
      v-if="!loading && fields.length > 0 && objects.length === 0"
      class="table-warning"
      style="font-size: 14px; text-align: center; margin-top: 5%"
    >
      {{ $t("NoObjectsOfTypeInSelectedArea") }}
    </div>

    <div v-if="loading" style="position:relative; width: 100%; height: 100px">
      <loading-spinner
        :active.sync="loading"
        :is-full-page="false"
        color="accent"
        background-color="transparent"
      />
    </div>
  </div>
</template>

<script>
import { bus } from "@/main";
import { mapActions } from "vuex";
import _ from "lodash";
import LoadingSpinner from "vue-loading-overlay";
import { multiSelectionMixin } from "@/mixins/multiSelectionMixin";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import { multipleObjectsTableMixin } from "@/mixins/forms/multipleObjectsSelection/multipleObjectsTableMixin";

export default {
  name: "MultipleObjectSingleLayerTable",

  components: { AppDialogBox, LoadingSpinner },

  mixins: [multiSelectionMixin, multipleObjectsTableMixin],

  props: {
    /**
     * Layer corresponding to the selected tab.
     */
    layer: {
      type: String,
      required: true
    },

    fieldFilters: {
      type: Array
    },

    canHighlightObjects: {
      type: Boolean,
      default: false
    },
    assetReservationObjects: {
      type: Array
    },

    assetReservationTemplate: {
      type: Object
    },
    /**
     * Indicates if only used assets should be listed
     */
    onlyUsedElements: {
      type: Boolean
    }
  },

  data() {
    return {
      loading: false,
      fields: [],
      options: {},
      prefix: undefined,

      originalObjects: [],

      editionMode: false,
      dataToSend: [],
      editSelectedOpen: false,

      closuresDelete: []
    };
  },

  computed: {
    /**
     * Determines the endpoint to use to make any requests.
     */
    endpoint() {
      return this.$store.getters.getLayerEndpoint(this.layer);
    },

    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberObjects() {
      return this.objects.length;
    },

    /**
     * Determines the number of objects selected
     * @return {Number}
     */
    numberSelectedObjects() {
      return this.selectedIndexes.length;
    },

    /**
     * Determines if any objects have been edited or not.
     */
    objectsWereEdited() {
      return this.dataToSend.length > 0;
    },

    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberFields() {
      return this.fields.length;
    },

    availableAssets() {
      if(this.originalObjects && this.assetReservationObjects){
        return this.originalObjects.filter(object => {
          return !this.assetReservationObjects.some(asset => {
            return asset.aro_object_uuid === object[this.prefix + '_uuid']
          });
        })
      } else {
        return this.originalObjects;
      }
    }
  },

  watch: {
    /**
     * Updates data structure when the object type is updated.
     */
    layer() {
      this.updateTable();
      bus.$emit("remove-highlight-from-selected-table");
    }
  },

  mounted() {
    this.updateTable();
  },

  destroyed() {
    bus.$emit("remove-highlight-from-selected-table");
  },

  methods: {
    ...mapActions([
      "FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE",
      "GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON",
      "DELETE_OBJECTS",
      "CLONE_OBJECTS",
      "EDIT_OBJECTS"
    ]),

    /**
     * Updates the table structure and content.
     */
    async updateTable() {
      this.clearDataToSend();

      this.loading = true;
      this.enableToolbar(false);
      try {
        await Promise.all([this.getTableStructure(), this.getObjects()]);
        this.enableToolbar(true);
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },

    /**
     * Retrieves the current tab object type from the store.
     */
    async getTableStructure() {
      this.fields = [];

      try {
        let data = await this.FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE({
          layer: this.layer,
          needsTabs: false,
          needsFields: true
        });

        let formFields = data.fields;
        this.prefix = data.initials;
        for (const field of formFields) {
          if (
            field.multi_select_show &&
            field.multi_select_label &&
            !field.hide
          ) {
            field.label = field.multi_select_label;
            if (field.input_type === "checkbox") {
              this.getCheckboxFields(field);
            } else if (
              field.input_type === "select" &&
              field.hasOwnProperty("select_list_name")
            ) {
              this.getSelectOptions(field.select_list_name);
            } else if (field.input_type === "radio") {
              this.getRadioOptions(field);
            }
            this.fields.push(field);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Obtains the objects of the currently selected layer present in the selected area.
     */
    async getObjects() {
      this.loading = true;
      this.enableToolbar(false);

      this.selectedIndexes = [];
      this.originalObjects = [];
      this.objects = [];

      let technicalType = undefined;
      if (this.endpoint.includes("technical")) {
        technicalType = this.layer;
      }

      try {
        let finalObjects = [];
        let objects = await this.GET_ALL_OBJECTS_IN_LAYER_WITHIN_POLYGON({
          endpoint: this.endpoint,
          technicalType: technicalType,
          fieldFilters: this.fieldFilters,
          fieldsToInclude: this.getFieldsToInclude(),
          usedByNetwork: this.onlyUsedElements
        });
        let filteredObjects = this.filterObjects(
          objects,
          this.layer.includes("base")
        );
        if (this.assetReservationTemplate.art_type == 'TPA') {
          filteredObjects.forEach(obj => {
            if (obj[`${this.prefix}_external_id`]) {
              finalObjects.push(obj)
            }
          })
        } else {
          finalObjects = [...filteredObjects];
        }
        if (
          this.assetReservationObjects &&
          this.assetReservationObjects.length > 0
        ) {

          finalObjects = this.filterAlreadyAddedAssets(
              this.assetReservationObjects,
              finalObjects
            );

        }
        this.originalObjects = JSON.parse(JSON.stringify(finalObjects));
        this.objects = finalObjects;
        this.enableToolbar(true);
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    //Filters assets that are already added
    filterAlreadyAddedAssets(assets, filteredObjects) {
      assets.forEach(asset => {
        filteredObjects = filteredObjects.filter(
          item => item[this.prefix + "_id"] !== asset.object_id
        );
      });

      return filteredObjects;
    },

    getFieldsToInclude() {
      switch (this.endpoint) {
        case "closures":
          return "node";
        case "buildings":
          return "technicalsite";
        case "sites":
        case "technicallocals":
          return "technicalsite";
        default:
          return "";
      }
    },

    /**
     * Gets only the objects corresponding to the given layer.
     * @param {Array} objectList - list of objects returned by the server
     * @param {Boolean} addIfBlocked - determines if it's the blocked or unblocked objects that are to be added to
     * the current list of objects.
     * @returns {Array}
     */
    filterObjects(objectList, addIfBlocked) {
      let blockedVar = this.prefix + "_blocked";

      return objectList.filter(
        object =>
          !object.hasOwnProperty(blockedVar) ||
          (addIfBlocked && object[blockedVar]) ||
          (!addIfBlocked && !object[blockedVar])
      );
    },

    getObjectUuidByIndex(index) {
      let object = this.objects[index];
      return object[this.prefix + "_uuid"];
    },

    /**
     * Creates a list of the UUIDs of the selected objects.
     */
    getSelectedObjectsUUIDs() {
      return this.selectedIndexes.map(objectIndex => {
        return {
          uuid: this.getObjectUuidByIndex(objectIndex)
        };
      });
    },

    toggleObjectHighlight(index, objectWasSelected) {
      let geoJson = this.getGeoJson(index);
      let uuid = this.getObjectUuidByIndex(index);

      if (uuid) {
        if (objectWasSelected) {
          bus.$emit("remove-highlight-from-selected-table", uuid);
        } else if (geoJson) {
          bus.$emit("highlight-object-from-selected-table", {
            geoJson: geoJson,
            uuid: uuid
          });
        }
      }
    },

    getGeoJson(index) {
      let object = this.objects[index];
      return this.searchObjectField(object, "geo_json");
    },

    searchObjectField(object, field) {
      for (const [key, value] of Object.entries(object)) {
        if (typeof value === "object" && value !== null) {
          let fieldFound = this.searchObjectField(value, field);
          if (fieldFound) return fieldFound;
        }
        if (key.includes(field)) {
          return value;
        }
      }
    },

    /**
     * Eliminates the selected objects from the database.
     */
    async removeObjects(listOfObjectsUUIDs) {
      try {
        await this.DELETE_OBJECTS({
          endpoint: this.endpoint,
          objects: listOfObjectsUUIDs
        });
        if (this.endpoint === "closures") {
          this.closuresDelete = {
            dialogOpen: false,
            associatedCables: null
          };
        }
        this.refresh();
        bus.$emit("update-geoserver");
      } catch (e) {
        console.error(e);

        if (this.endpoint === "closures") {
          this.closuresDelete = {
            dialogOpen: true,
            associatedCables: e.response.data.data,
            selected: this.selectedIndexes
          };
        }
      }
      this.$nextTick(() => this.$emit("closureDelete", this.closuresDelete));
    },

    /**
     * Clones the selected objects (creates new objects with the same information)
     */
    async clone() {
      try {
        await this.CLONE_OBJECTS({
          endpoint: this.endpoint,
          data: this.getSelectedObjectsUUIDs()
        });
        this.refresh();
        bus.$emit("update-geoserver");
      } catch (e) {
        console.log(e);
      }
    },

    /**
     * Updates the state of the edition mode.
     * @param {Boolean} isActive - if the edition mode is active or not
     */
    updateMode(isActive) {
      if (this.objectsWereEdited) {
        this.clearDataToSend();
        this.refresh();
      }
      this.editionMode = isActive;
    },

    /**
     * Getd the feld value in non edition mode.
     * @param {Object} object
     * @param {Object} field
     * @returns {Number | String}
     */
    getNonEditableValue(object, field) {
      let value = this.getValue(object, field);
      if (value === null || value === undefined || value === "") {
        value = "-";
      } else if (field.input_type === "select" && field.select_list_name) {
        value = this.getOptionLabel(field.select_list_name, value);
      } else if (field.input_type === "radio" && field.var_name) {
        value = this.getOptionLabel(field.var_name, value);
      }
      return value;
    },

    /**
     * Gets the label corresponding to the value of a given select variable
     * @param {Object} optionsList - list being analysed
     * @param id
     * @return {String | Number}
     */
    getOptionLabel(optionsList, id) {
      let list = this.options[optionsList];
      if (list !== undefined) {
        let option = list.find(item => item.id == id);
        if (option) {
          return option.label;
        }
      }
      return id;
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getCheckboxFields(field) {
      if (field.hasOwnProperty("checkbox_options")) {
        for (const option of field.checkbox_options) {
          let checkboxField = option;
          checkboxField.input_type = field.input_type;
          this.fields.push(checkboxField);
        }
      }
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getRadioOptions(field) {
      if (field.hasOwnProperty("var_name") && field.var_name) {
        let varName = field.var_name;
        this.options[varName] = [];
        if (field.hasOwnProperty("radio_options")) {
          for (const option of field.radio_options) {
            let radioOption = {
              id: option.value,
              label: option.label
            };
            this.options[varName].push(radioOption);
          }
        }
      }
    },

    /**
     * Obtains the options for a given select list.
     * @param {String} listName - name of the list to use
     */
    getSelectOptions(listName) {
      if (
        !this.options.hasOwnProperty(listName) ||
        this.options[listName].length === 0
      ) {
        let list = this.$store.getters.getListOfOptionsByListName(
          listName,
          [],
          true
        );

        if (list) {
          this.options[listName] = [];
          for (const option of list) {
            this.options[listName].push(this.normalizer(option));
          }
        }
      }
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param {Object} node - option being normalized.
     * @returns {Object}
     */
    normalizer(node) {
      let id = "";
      let label = "";
      switch (true) {
        case node.hasOwnProperty("code"):
          id = node.code;
          if (node.hasOwnProperty("client_label") && node.client_label) {
            label = node.client_label;
          } else if (node.hasOwnProperty("libelle") && node.libelle) {
            label = node.libelle;
          } else if (node.hasOwnProperty("reference") && node.reference) {
            //cableMaterialList
            label = node.reference;
          } else if (node.hasOwnProperty("name") && node.name) {
            //cableMaterialList
            label = node.name;
          } else if (
            node.hasOwnProperty("municipality_label") &&
            node.municipality_label
          ) {
            label = node.municipality_label;
          }
          break;
        case node.hasOwnProperty("or_id"): //OwnerList
          id = node.or_id;
          label = node.or_nom;
          break;
        case node.hasOwnProperty("pw_id"): //PathwayIdsList
          id = node.pw_id;
          label = node.pw_id;
          break;
      }
      return {
        id: id,
        label: label,
        isDisabled: node.deleted
      };
    },

    /**
     * Checks if the value of a field is different from the original one.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    valueWasChanged(objectIndex, field) {
      let originalValue = this.originalObjects[objectIndex][field.var_name];
      let newValue = this.objects[objectIndex][field.var_name];

      if (
        originalValue === null &&
        (newValue === "" || newValue === undefined || newValue === null)
      ) {
        return false;
      } else {
        return originalValue != newValue;
      }
    },

    /**
     * Checks if the value of a required field was removed.
     * @param {Number} objectIndex - index of the edited object
     * @param {Object} field - field being edited
     * @returns {boolean}
     */
    requiredWasMadeEmpty(objectIndex, field) {
      return field.required && !this.objects[objectIndex][field.var_name];
    },

    /**
     * Gets the original value of a given variable
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {Object} field - field being analysed
     * @param value
     * @return {String | Number}
     */
    setValue(objectIndex, field, value) {
      let object = this.objects[objectIndex];
      _.set(object, field.var_name, value);
      this.updateDataToSend(objectIndex, field.var_name, value);
    },

    /**
     * Places the new value in an array to send to the server.
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {String} varName
     * @param {String | Number} newValue
     */
    updateDataToSend(objectIndex, varName, newValue) {
      let uuid = this.getObjectUuidByIndex(objectIndex);
      let newVarName = varName.replace(this.prefix + "_", "");
      let editedObjectIndex = this.dataToSend.findIndex(
        editedObject => editedObject.uuid === uuid
      );

      if (editedObjectIndex > -1) {
        this.dataToSend[editedObjectIndex][newVarName] = newValue;
      } else this.dataToSend.push({ uuid: uuid, [newVarName]: newValue });
    },

    closeEditSelected() {
      this.editSelectedOpen = false;
      bus.$emit("deactivateEditSelected");
    },

    /**
     * Applies the new values to all the selected objects
     * @param {Object} newValues
     */
    applyMassEdition(newValues) {
      this.closeEditSelected();
      this.selectedIndexes.forEach(objectIndex => {
        for (let key in newValues) {
          _.set(this.objects[objectIndex], key, newValues[key]);
          this.updateDataToSend(objectIndex, key, newValues[key]);
        }
      });
    },

    /**
     * Saves the data that was changed.
     */
    save() {
      if (this.dataToSend.length !== 0) {
        this.EDIT_OBJECTS({
          endpoint: this.endpoint,
          data: this.dataToSend
        })
          .then(() => this.updateTable())
          .catch(error => console.error(error));
      } else this.updateTable();
    },

    /**
     * Refresh the data shown.
     */
    refresh() {
      this.getObjects();
      bus.$emit("remove-highlight-from-selected-table");
    },

    /**
     * Clear the data to send object.
     */
    clearDataToSend() {
      this.dataToSend = [];
    },

    getInputRef(objectIndex, fieldIndex) {
      return objectIndex * 1000 + fieldIndex;
    },

    changePositionFocus(objectIndex, fieldIndex, valueChange) {
      let ref = this.getInputRef(objectIndex, fieldIndex);
      let nextRef = ref + valueChange;
      let nextFieldIndex = nextRef % 1000;
      let nextObjectIndex = (nextRef - nextFieldIndex) / 1000;

      if (
        nextObjectIndex >= 0 &&
        nextObjectIndex < this.numberObjects &&
        nextFieldIndex >= 0 &&
        nextFieldIndex < this.numberFields
      ) {
        let input = this.$refs[nextRef][0].querySelector("input");
        if (input === null || input.disabled) {
          nextRef = nextRef + valueChange;
          this.changePositionFocus(
            nextObjectIndex,
            nextFieldIndex,
            valueChange
          );
        } else {
          input.focus();
        }
      }
    },

    //Standby because with many objects the application blocks
    sortTable(n) {
      let table,
        rows,
        i,
        x,
        y,
        shouldSwitch,
        switchcount = 0;
      table = document.getElementById("myTable");
      let switching = true;
      //Set the sorting direction to ascending:
      let dir = "asc";
      /*Make a loop that will continue until no switching has been done:*/
      while (switching) {
        //start by saying: no switching is done:
        switching = false;
        rows = table.rows;
        /*Loop through all table rows (except the first, which contains table headers):*/
        for (i = 1; i < rows.length - 1; i++) {
          //start by saying there should be no switching:
          shouldSwitch = false;
          /*Get the two elements you want to compare, one from current row and one from the next:*/
          x = rows[i].getElementsByTagName("TD")[n];
          y = rows[i + 1].getElementsByTagName("TD")[n];
          /*check if the two rows should switch place, based on the direction, asc or desc:*/
          if (dir === "asc") {
            if (x.innerHTML.toLowerCase() > y.innerHTML.toLowerCase()) {
              //if so, mark as a switch and break the loop:
              shouldSwitch = true;
              break;
            }
          } else if (dir === "desc") {
            if (x.innerHTML.toLowerCase() < y.innerHTML.toLowerCase()) {
              //if so, mark as a switch and break the loop:
              shouldSwitch = true;
              break;
            }
          }
        }
        if (shouldSwitch) {
          /*If a switch has been marked, make the switch and mark that a switch has been done:*/
          rows[i].parentNode.insertBefore(rows[i + 1], rows[i]);
          switching = true;
          //Each time a switch is done, increase this count by 1:
          switchcount++;
        } else {
          /*If no switching has been done AND the direction is "asc",
      set the direction to "desc" and run the while loop again.*/
          if (switchcount === 0 && dir === "asc") {
            dir = "desc";
            switching = true;
          }
        }
      }
    },

    enableToolbar(enable) {
      this.$emit("enable-toolbar", enable);
    },

    getSelectedObjects() {
      return this.selectedIndexes.map(index => this.availableAssets[index]);
    },

    highlightObject(objectIndex) {
      let geoJson = this.getGeoJson(objectIndex);
      let uuid = this.getObjectUuidByIndex(objectIndex);
      if (geoJson) {
        bus.$emit("highlight-object-from-selected-table", {
          geoJson: geoJson,
          uuid: uuid
        });
      }
    },

    resetSelectedIndexes() {
      this.selectedIndexes = [];
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/_Organisms/MultipleObjectsSingleLayerTable.vue